import React, { FunctionComponent, useEffect, useMemo } from 'react';
import classNames from 'classnames';
import {
  DefaultFinanceTerms,
  FinanceType,
} from '@upstart/harmony/lib/constants';
import { cloneDeep } from 'lodash';
import { CreditRatingMax } from '@upstart/harmony/lib/legos/constants';
import { DealerLaunchStepProps } from '../types';
import { useLaunchpadForm } from '../../../hooks/useLaunchpadForm';
import { DealerLaunchFormCardLayout } from '../DealerLaunchFormCardLayout';
import {
  TextField,
  TextFieldValidationState,
} from '../../Input/TextField/TextField';
import styles from '../styles.module.scss';
import { TooltipIcon } from '../../TooltipIcon/TooltipIcon';
import { DEFAULT_CREDIT_TIERS, DEFAULT_PURCHASE_PRICING } from '../defaults';
import {
  CreditPricingPurchasePricing,
  CreditPricingPurchaseTerm,
} from '@upstart/harmony/lib/legos/creditPricingType';

const decimalNumberValidation = (
  data: string | undefined,
): TextFieldValidationState => {
  const valid = !!data?.match(/^\d+(\.\d+)?%$/) && parseFloat(data) !== 0;
  return !valid
    ? {
        valid: false,
        message: 'Invalid Number',
      }
    : { valid: true };
};

const termValidation = (
  data: string | undefined,
  terms: { months: string }[],
): TextFieldValidationState => {
  if (terms.filter((t) => t.months === data).length > 1) {
    return {
      valid: false,
      message: 'Duplicate Term',
    };
  }
  const valid = !!data?.match(/^\d+$/) && data !== '0';

  return !valid
    ? {
        valid: false,
        message: 'Invalid Number',
      }
    : { valid: true };
};

const validatePricing = (
  pricing: { apr: string }[][],
  terms: { months: string }[],
) => {
  return (
    !pricing.some((row) =>
      row.some((p) => {
        return !decimalNumberValidation(p?.apr).valid;
      }),
    ) && !terms.some((term) => !termValidation(term.months, terms).valid)
  );
};

export const DealerLaunchFinancePricingRates: FunctionComponent<DealerLaunchStepProps> = ({
  dealer,
  prevStep,
  nextStep,
}) => {
  const creditTiers =
    dealer.launchpad?.creditPricing?.[FinanceType.PURCHASE]?.creditTiers ||
    DEFAULT_CREDIT_TIERS;
  const defaultTerms = useMemo(() => {
    return (
      dealer.launchpad?.creditPricing?.[
        FinanceType.PURCHASE
      ]?.terms?.map((t) => ({ months: t.months.toString() })) ||
      DefaultFinanceTerms.PURCHASE.map((months) => ({
        months: months.toString(),
      }))
    );
  }, [dealer]);

  const defaultPricing = useMemo(() => {
    const savedPricing = (
      dealer.launchpad?.creditPricing?.[FinanceType.PURCHASE]?.pricing ||
      DEFAULT_PURCHASE_PRICING
    ).map(
      (row) =>
        row
          .map((t, idx) =>
            idx < defaultTerms.length
              ? { apr: `${t.apr.toString()}%` }
              : undefined,
          )
          .filter((p) => !!p) as { apr: string }[],
    );

    if (savedPricing?.length !== undefined) {
      while (savedPricing.length < creditTiers.length) {
        savedPricing.push(
          new Array(defaultTerms.length).fill(0).map(() => ({
            apr: '',
          })),
        );
      }
      while (savedPricing.length > creditTiers.length) {
        savedPricing.pop();
      }
    }
    return savedPricing;
  }, [creditTiers, defaultTerms, dealer]);

  const form = useLaunchpadForm({
    defaultValues: {
      terms: defaultTerms,
      pricing: defaultPricing,
    },
  });

  useEffect(() => {
    form.register('terms');
    form.register('pricing');
  }, [form]);

  const pricing = form.watch('pricing') || [];
  const terms = form.watch('terms') || [];

  const handleChange = (rowIdx: number, colIdx: number, value: string) => {
    const newPricing = cloneDeep(pricing);
    newPricing[rowIdx][colIdx].apr = value;
    form.setValue(`pricing`, newPricing, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };
  const handleBlur = (rowIdx: number, colIdx: number) => {
    if (
      !pricing[rowIdx][colIdx].apr ||
      pricing[rowIdx][colIdx].apr?.endsWith('%')
    ) {
      return;
    }
    const newPricing = cloneDeep(pricing);
    const floatValue = parseFloat(newPricing[rowIdx][colIdx].apr);
    // eslint-disable-next-line no-restricted-globals
    newPricing[rowIdx][colIdx].apr = isNaN(floatValue) ? '' : `${floatValue}%`;
    form.setValue(`pricing`, newPricing, {
      shouldValidate: true,
      shouldDirty: true,
    });
  };

  const handleTermChange = (idx: number, value: string) => {
    const clonedTerms = cloneDeep(terms);
    const termValue = parseInt(value, 10);
    // eslint-disable-next-line no-restricted-globals
    clonedTerms[idx].months = (isNaN(termValue) ? '' : termValue).toString();
    form.setValue('terms', clonedTerms);
  };

  const onSubmit = async (data: {
    pricing: { apr: string }[][];
    terms: { months: string }[];
  }) => {
    if (!validatePricing(pricing, terms)) {
      return;
    }

    const creditPricingForPurchase = {
      pricing: data.pricing.map((row) =>
        row.map((item) => ({
          markup: 0,
          apr: parseFloat(item.apr),
        })),
      ) as CreditPricingPurchasePricing[][],
      terms: data.terms.map((t) => ({
        months: parseInt(t.months, 10),
      })) as CreditPricingPurchaseTerm[],
    };

    await nextStep({
      launchpad: {
        creditPricing: {
          [FinanceType.PURCHASE]: {
            ...creditPricingForPurchase,
            creditTiers,
          },
        },
      },
    });
  };

  const renderHeader = () => (
    <>
      <div />
      {terms.map((term, idx) => {
        return (
          /* eslint-disable-next-line react/no-array-index-key */
          <div key={idx} className={classNames(styles.ratesInputCell)}>
            {idx !== 0 && <div className={styles.borderLeft} />}
            <TextField
              label={`Term ${['One', 'Two', 'Three'][idx]}`}
              dataTestId={`terms.${idx}.months`}
              addOn="Months"
              width="sm"
              value={term.months}
              onChange={(ev) => handleTermChange(idx, ev.target.value)}
              validationState={termValidation(term.months, terms)}
            />
          </div>
        );
      })}
    </>
  );

  const renderRateRow = (pricingRow: { apr?: string }[], rowIndex: number) => {
    const high =
      rowIndex === 0
        ? CreditRatingMax
        : (creditTiers?.[rowIndex - 1].value || 1) - 1;

    return (
      <React.Fragment key={rowIndex}>
        <div className={styles.ratesInputCell}>
          <div>
            <span className={styles.tooltipLabel}>
              Tier {rowIndex + 1} APR %
            </span>
            <TooltipIcon
              content={`Credit Range ${creditTiers?.[rowIndex].value} - ${high}`}
            />
          </div>
        </div>
        {pricingRow.map(({ apr }, idx: number) => {
          return (
            <div
              /* eslint-disable-next-line react/no-array-index-key */
              key={`${rowIndex}+${idx}`}
              className={classNames(
                styles.ratesInputCell,
                styles.tier,
                rowIndex === 0 && styles.top,
                idx === pricingRow.length - 1 && styles.right,
              )}
            >
              <TextField
                className={classNames(styles.rateInput)}
                labelClassName={styles.rateInputLabel}
                iconClassName={styles.rateInputIcon}
                label=""
                onChange={(event) => {
                  handleChange(rowIndex, idx, event.target.value);
                }}
                dataTestId={`apr-${rowIndex}-${idx}`}
                width="sm"
                value={apr}
                required
                validationState={decimalNumberValidation(apr)}
                onBlur={() => {
                  handleBlur(rowIndex, idx);
                }}
              />
            </div>
          );
        })}
      </React.Fragment>
    );
  };

  return (
    <DealerLaunchFormCardLayout
      className={styles.pricingRatesStep}
      form={form}
      title="Tell Us About Your Finance Options"
      subTitle="Your APR will impact the monthly payment amount for finance customers. We've already filled in our recommended rates, but you can always customise them further."
      cardTitle="Set your annual percentage rates for finance purchases"
      prevStep={prevStep}
      onFormSubmit={onSubmit}
    >
      <div className={styles.ratesGrid}>
        {renderHeader()}
        {pricing?.map(renderRateRow)}
      </div>
    </DealerLaunchFormCardLayout>
  );
};
